import type React from 'react';
import styled, { useTheme } from 'styled-components';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';

import HyroxLogo from 'src/app/ui/components/atoms/icons/hyrox-logo';
import Typography from 'app/ui/components/atoms/typography';
import { routes } from 'app/routes/constants';

const FooterContainer = styled.footer`
  width: 100%;
  margin-top: auto;

  ${({ theme: { colors } }) => `
    background: ${colors.black};
    color: ${colors.white};
  `}
`;

const FooterContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: ${({ theme: { spacing } }) => spacing(19)};
  margin: 0 auto;
  gap: ${({ theme: { spacing } }) => spacing(4)};

  ${({ theme: { media, spacing } }) => `
    ${media.small(
      () => `
        padding: ${spacing(6)};
      `,
    )}
    ${media.large(
      () => `
        flex-direction: row;
        justify-content: space-between;
        padding: ${spacing(6)};
        max-width: 67.5rem;
        gap: ${spacing(8)};
      `,
    )}
  `}
`;

const FooterWithBorder = styled.div`
  width: 100%;
  border-top: 1px solid ${({ theme: { colors } }) => colors.veryGray};

  ${({ theme: { spacing } }) => `
    margin-top: ${spacing(8)};
  `}
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  
  svg {
    width: ${({ theme: { spacing } }) => spacing(26)};
  }

  margin-bottom: ${({ theme: { spacing } }) => spacing(4)};

  ${({ theme: { media, spacing } }) => `
    ${media.large(
      () => `
        svg {
          width: ${spacing(30)};
        }
        margin-bottom: 0;
      `,
    )}
  `}
`;

const LinksSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(4)};

  ${({ theme: { media, spacing } }) => `
    ${media.large(
      () => `
        flex-direction: row;
        gap: ${spacing(8)};
        width: 100%;
        justify-content: space-between;
        padding-left: ${spacing(6)};
      `,
    )}
  `}
`;

const LinkGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(4)};

  ${({ theme: { media, spacing } }) => `
    ${media.large(
      () => `
        flex-direction: row;
        gap: ${spacing(10)};
      `,
    )}
  `}
`;

const FooterLink = styled(Link)`
  text-decoration: none;
`;

const ExternalLink = styled.a`
  text-decoration: none;
`;

const Footer: React.FC = () => {
  const theme = useTheme();
  const intl = useIntl();

  return (
    <FooterWithBorder>
      <FooterContainer>
        <FooterContentWrapper>
          <Link to="/">
            <LogoWrapper>
              <HyroxLogo />
            </LogoWrapper>
          </Link>

          <LinksSection>
            <LinkGroup>
              <ExternalLink href="mailto:support@hyrox365.com">
                <Typography variant="body2" color={theme.colors.white}>
                  {intl.formatMessage({ id: 'contact.buttons.contact' })}
                </Typography>
              </ExternalLink>
              <FooterLink to={routes.FAQ}>
                <Typography variant="body2" color={theme.colors.white}>
                  {intl.formatMessage({ id: 'faq.header' })}
                </Typography>
              </FooterLink>
            </LinkGroup>

            <LinkGroup>
              <FooterLink to={routes.TERMS_CONDITIONS}>
                <Typography variant="body2" color={theme.colors.lightGray}>
                  {intl.formatMessage({ id: 'cookieConsent.links.termsConditions' })}
                </Typography>
              </FooterLink>
              <ExternalLink href="https://fiit.tv/privacy" target="_blank" rel="noopener noreferrer">
                <Typography variant="body2" color={theme.colors.lightGray}>
                  {intl.formatMessage({ id: 'cookieConsent.links.privacyPolicy' })}
                </Typography>
              </ExternalLink>
            </LinkGroup>
          </LinksSection>
        </FooterContentWrapper>
      </FooterContainer>
    </FooterWithBorder>
  );
};

export default Footer;
